import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { log } from '../lib/logger';
import type { TeamInvite, InviteRole, CreateInviteResponse } from '../types/teamInvites';

export function useTeamInvites(businessId: string | undefined) {
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const fetchInvites = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from('team_invites')
      .select('id, business_id, code, role, invited_name, invited_by, claimed_by, claimed_at, expires_at, revoked_at, created_at')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });
    if (!mountedRef.current) return;
    if (!error && data) setInvites(data as TeamInvite[]);
    setLoading(false);
  }, [businessId]);

  useEffect(() => {
    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchInvites();
    return () => { mountedRef.current = false; };
  }, [fetchInvites]);

  useEffect(() => {
    if (!businessId) return;
    const channel = supabase
      .channel(`admin-team-invites-${businessId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'team_invites', filter: `business_id=eq.${businessId}` }, () => {
        void fetchInvites();
      })
      .subscribe((status, err) => {
        if (status !== 'SUBSCRIBED') {
          log.warn('[realtime] subscribe non-OK', { channel: `admin-team-invites-${businessId}`, status, err });
        }
      });
    return () => { void supabase.removeChannel(channel); };
  }, [businessId, fetchInvites]);

  const createInvite = useCallback(async (
    name: string, role: InviteRole,
  ): Promise<CreateInviteResponse> => {
    if (!businessId) return { success: false, error_code: 'not_authenticated' };
    const { data, error } = await supabase.rpc('create_team_invite', {
      p_business_id: businessId,
      p_invited_name: name,
      p_role: role,
    });
    if (error) return { success: false, error_code: 'network_error' };
    const result = data as CreateInviteResponse;
    if (result.success) void fetchInvites();
    return result;
  }, [businessId, fetchInvites]);

  const revokeInvite = useCallback(async (inviteId: string): Promise<{ success: boolean; error_code?: string }> => {
    const { data, error } = await supabase.rpc('revoke_team_invite', { p_invite_id: inviteId });
    if (error) return { success: false, error_code: 'network_error' };
    const result = data as { success: boolean; error_code?: string };
    if (result.success) void fetchInvites();
    return result;
  }, [fetchInvites]);

  const resendInvite = useCallback(async (invite: TeamInvite): Promise<CreateInviteResponse> => {
    await revokeInvite(invite.id);
    return createInvite(invite.invited_name, invite.role);
  }, [revokeInvite, createInvite]);

  return { invites, loading, refresh: fetchInvites, createInvite, revokeInvite, resendInvite };
}
